import type { Brand } from "@tento-ui/brand/brand";
import type { BlogPost, CardLayout } from "@tento-ui/brand/blog/blog-types";
import { Column, Row } from "@tento-ui/component-builtin";
import { Typography } from "@tento-ui/ui/typography";
import { NaiveDate } from "@tento-chrono";
import "@tento-ui/brand/blog/blog-claude-card";
import "@tento-ui/brand/blog/blog-list-card";
import { BlogClaudeCard } from "@tento-ui/brand/blog/blog-claude-card";
import { BlogListCard } from "@tento-ui/brand/blog/blog-list-card";

type PostContent = Brand.BlogPage["blog"]["post"];

function noop<T>($el: T): T { return $el; }

// ─── Helpers ───

function renderDate(input: string): string {
  const parts = input.split("-");
  if (parts.length !== 3) return input;
  const year = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10);
  const day = Number.parseInt(parts[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return input;
  try {
    const date = NaiveDate.fromYmd1(year, month, day).exp();
    return `${date.month.shortName} ${date.day1}, ${date.yr}`;
  } catch {
    return input;
  }
}

// ─── Claude content ───

export const claudeContent: PostContent = {
  title: {
    make(post) {
      const $title = Typography.h1(post.title);
      $title.classList.add("blog-post-title");
      $title.style.setProperty("--margin-top", "0px");
      $title.style.setProperty("--margin-bottom", "24px");
      return $title;
    },
    bind: noop,
  },
  meta: {
    make(post) {
      const $col = Column.make();
      $col.classList.add("blog-post-meta");
      $col.assignStyles({ gap: "16px", alignItems: "center" });

      const $metaRow = Row.make();
      $metaRow.classList.add("blog-post-meta-row");

      const $date = Typography.small(renderDate(post.date));
      $date.classList.add("blog-post-date");
      $metaRow.appendChild($date);

      if (post.category) {
        const $category = Typography.small(post.category);
        $category.classList.add("blog-post-category");
        $metaRow.appendChild($category);
      }

      $col.appendChild($metaRow);

      if (post.tags && post.tags.length > 0) {
        const $tagRow = Row.make();
        $tagRow.classList.add("tag-list");
        $tagRow.assignStyles({ gap: "6px", flexWrap: "wrap", justifyContent: "center" });
        for (const tag of post.tags) {
          const $tag = Typography.small(tag);
          $tag.classList.add("tag");
          $tag.style.setProperty("--font-size", "var(--text-11px)");
          $tag.style.setProperty("--margin-top", "0px");
          $tag.style.setProperty("--margin-bottom", "0px");
          $tag.assignStyles({
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 11px",
            borderRadius: "999px",
            border: "1px solid var(--divider)",
            backgroundColor: "var(--bg-surface-light)",
            color: "var(--text-sec)",
            fontFamily: "sans-serif",
          });
          $tagRow.appendChild($tag);
        }
        $col.appendChild($tagRow);
      }

      return $col;
    },
    bind: noop,
  },
  card: {
    make({ post, layout }) { return BlogClaudeCard.makeWith({ post, layout }); },
    bind: noop,
  },
};

// ─── List content ───

export const listContent: PostContent = {
  title: {
    make(post) {
      const $title = Typography.h1(post.title);
      $title.classList.add("blog-post-title");
      return $title;
    },
    bind: noop,
  },
  meta: {
    make(post) {
      const $col = Column.make();
      $col.classList.add("blog-post-meta");

      const $date = Typography.small(renderDate(post.date));
      $date.classList.add("blog-post-date");
      $col.appendChild($date);

      return $col;
    },
    bind: noop,
  },
  card: {
    make({ post, layout }) { return BlogListCard.makeWith({ post, layout }); },
    bind: noop,
  },
};

// ─── Bear content ───

export const bearContent: PostContent = {
  title: {
    make(post) {
      const $title = document.createElement("h1");
      $title.className = "blog-post-title";
      $title.textContent = post.title;
      return $title;
    },
    bind: noop,
  },
  meta: {
    make(post) {
      const $date = document.createElement("time");
      $date.classList.add("blog-post-date");
      $date.textContent = post.date;
      return $date;
    },
    bind: noop,
  },
  card: {
    make({ post }) {
      const $el = document.createElement("div");
      $el.textContent = post.title;
      return $el;
    },
    bind: noop,
  },
};

// ─── Minimal content ───

export const minimalContent: PostContent = {
  title: {
    make(post) {
      const $title = Typography.h1(post.title);
      $title.classList.add("blog-post-title");
      $title.style.setProperty("--margin-top", "0px");
      $title.style.setProperty("--margin-bottom", "16px");
      return $title;
    },
    bind: noop,
  },
  meta: {
    make(post) {
      const $col = Column.make();
      $col.classList.add("blog-post-meta");
      $col.assignStyles({ gap: "8px" });

      const $date = document.createElement("span");
      $date.classList.add("blog-post-date");
      $date.textContent = post.date;
      $col.appendChild($date);

      if (post.tags && post.tags.length > 0) {
        const $tagRow = Row.make();
        $tagRow.classList.add("tag-list");
        $tagRow.assignStyles({ gap: "8px", flexWrap: "wrap" });
        for (const tag of post.tags) {
          const $tag = document.createElement("span");
          $tag.classList.add("tag");
          $tag.textContent = tag;
          $tagRow.appendChild($tag);
        }
        $col.appendChild($tagRow);
      }

      return $col;
    },
    bind: noop,
  },
  card: {
    make({ post }) {
      const $el = document.createElement("div");
      $el.textContent = post.title;
      return $el;
    },
    bind: noop,
  },
};
